/**
 * Textos de interfaz y documentos fiscales — tono formal de sistema (ERP / AFIP).
 * No usar voseo ni marketing en estos strings.
 */

export const FISCAL_SYSTEM_COPY = {
  /** Estados del comprobante en listados y detalle */
  status: {
    nonFiscal: 'No fiscal',
    issued: 'Comprobante autorizado',
    rejected: 'Rechazado por AFIP',
    retryQueued: 'Reintento programado',
    pendingAuthorization: 'Pendiente de autorización',
    emissionError: 'Error de emisión fiscal',
  },

  /** Mensajes de error / aviso */
  errors: {
    defaultRejection:
      'La solicitud de autorización fue rechazada. Verifique los datos del receptor en Ventas > Comprobante fiscal.',
    pendingNoCae:
      'La venta quedó registrada como fiscal, pero AFIP no devolvió el C.A.E. en el plazo de espera. Consulte el estado en Ventas > Comprobante fiscal.',
    padronHint:
      'Modifique el receptor fiscal (Consumidor final o CUIT inscripto en el padrón) y seleccione «Reintentar emisión». Si la operación ya fue registrada, puede anular la venta y volver a facturar.',
  },

  /** Cobro POS — toasts */
  checkout: {
    emittingTitle: 'Emisión en curso',
    emittingBody: 'Solicitando autorización electrónica en AFIP…',
    rejectedTitle: 'Comprobante rechazado',
    pendingTitle: 'Emisión fiscal pendiente',
    saleRegisteredWithFiscalPending:
      ' · Comprobante fiscal pendiente de autorización — consulte Ventas > Comprobante fiscal',
  },

  /** Ticket interno sin C.A.E. */
  internalReceipt: {
    notFiscalDocument: 'Documento interno. No constituye comprobante fiscal.',
    invoiceWillFollow:
      'La factura electrónica se emitirá cuando AFIP autorice la operación. Conserve este número para solicitarla en el local.',
    accountBalancePrefix: 'Saldo a cuenta:',
  },

  /** Detalle ventas — sección */
  panel: {
    sectionTitle: 'Comprobante fiscal',
    saveBuyer: 'Guardar receptor',
    saveAndRetry: 'Guardar y reintentar emisión',
    editBuyer: 'Modificar receptor fiscal',
    buyerSaved: 'Receptor fiscal actualizado',
    buyerSavedRetry: 'Receptor actualizado. Reintentando emisión…',
    viewQrArca: 'Ver código QR (ARCA)',
    constatarArca: 'Constatar en ARCA',
    constatarArcaHomologacion: 'Constatar en ARCA (homologación)',
    printThermal: 'Imprimir comprobante',
    downloadPdf: 'Descargar PDF',
    copySummary: 'Copiar datos del comprobante',
    retryEmission: 'Reintentar emisión',
    homologacionBanner:
      'Comprobante de prueba — sin validez fiscal. El portal de verificación de ARCA no aplica en homologación.',
    mobilePdfHint:
      'En dispositivos móviles puede guardar el PDF en Archivos o compartirlo mediante otras aplicaciones.',
    constatacionTitle: 'Constatación ARCA',
  },

  /** Receptor en cobro y en detalle de venta */
  buyerSection: {
    sectionTitle: 'Receptor fiscal',
    hintWithClient: 'Cliente de la venta, consumidor final u otro CUIT.',
    hintNoClient: 'Consumidor final u otro CUIT (consulta de padrón en ARCA).',
    consumidorFinal: 'Consumidor final',
    otroCuit: 'Otro CUIT',
    consultArca: 'Consultar en ARCA',
    consultArcaA11y: 'Consultar padrón ARCA por CUIT',
    branchRequired: 'Seleccione una sucursal activa.',
    cuitInvalid: 'Ingrese un CUIT de 11 dígitos.',
    padronFailed: 'No fue posible consultar el padrón.',
    padronRetry: 'No fue posible consultar el padrón. Intente nuevamente.',
    padronNoRazonSocial: 'No se obtuvo razón social del padrón.',
    linkedCustomer: 'Cliente vinculado',
    occasionalCustomer: 'Cliente eventual',
    sandboxPadron: 'Modo de prueba: la razón social puede diferir de ARCA.',
  },

  creditNote: {
    issued: 'Nota de crédito autorizada',
    pending: 'Nota de crédito pendiente de autorización',
    error: 'Error en la emisión de la nota de crédito',
    notRequired: 'Nota de crédito no requerida',
    notIssuedYet: 'La nota de crédito aún no tiene C.A.E.',
    printFailed: 'No fue posible imprimir la nota de crédito.',
    retryFailed: 'No fue posible reintentar la emisión de la nota de crédito.',
    retryEmission: 'Reintentar nota de crédito',
  },

  debitNote: {
    amountPlaceholder: 'Importe (ej.: 1500,50)',
    amountInvalid: 'Ingrese un importe mayor a cero.',
    emitPending:
      'La emisión no se completó. El comprobante quedó pendiente de reintento automático.',
    emitSuccessTitle: 'Nota de débito autorizada',
    emitSuccessBody: 'El comprobante tiene C.A.E.',
    emitFailed: 'No fue posible emitir la nota de débito.',
    printFailed: 'No fue posible imprimir la nota de débito.',
  },

  /** Errores genéricos del panel */
  panelErrors: {
    exportFailed: 'No fue posible exportar el comprobante.',
    consultFailed: 'No fue posible constatar el comprobante en ARCA.',
    retryFailed: 'No fue posible reintentar la emisión.',
    buyerUpdateFailed: 'No fue posible actualizar el receptor fiscal.',
    printFailed: 'No fue posible imprimir el comprobante.',
  },

  /** Poll / CAE obtenido */
  detail: {
    caeAuthorizedTitle: 'Comprobante autorizado',
    caeAuthorizedBody:
      'AFIP otorgó el C.A.E. Ya puede imprimir o exportar el comprobante en PDF.',
  },
} as const;

/** Pie del ticket interno cuando la venta es fiscal y aún no hay C.A.E. */
export function buildFiscalPendingInternalReceiptLines(accountPendingLabel?: string | null): string[] {
  const lines: string[] = [
    FISCAL_SYSTEM_COPY.internalReceipt.notFiscalDocument,
    FISCAL_SYSTEM_COPY.internalReceipt.invoiceWillFollow,
  ];
  if (accountPendingLabel?.trim()) {
    lines.push(`${FISCAL_SYSTEM_COPY.internalReceipt.accountBalancePrefix} ${accountPendingLabel.trim()}`);
  }
  return lines;
}
